/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import { IAftermarketReturnPolicy } from "api/interfaces/static-pages/aftermarket-return-policy.type";
import CmsContent from "component/CmsContent";
import Element from "component/design-system/react-scroll/components/Element";
import Text from "component/design-system/typography/Text";
import FaqAccordion from "container/FaqAccordion";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import { memo, useState } from "react";
import PAButton, { BUTTON_SIZE } from "../../component/design-system/buttons/PAButton";
import LandingPageBanner from "../../component/landing-page/LandingPageBanner";
import PageContainer from "../../modules/components/PageContainer";
import { getRelativeImagePath } from "../../util/helper/getRelativeImagePath";
import { scrollTo } from "../../util/scroll-helper";
import ChatReturn from "./ChatReturn";
import styles from "./returns.module.scss";

const returnProcedure = [
  {
    step: "return_step_one",
  },
  {
    step: "return_step_two",
  },
  {
    step: "return_step_three",
  },
  {
    step: "return_step_four",
  },
  {
    step: "return_step_five",
  },
];

const returnPolicyDefination = [
  "aftermarket-parts-return-policy-defination",
  "fr-aftermarket-parts-return-policy-defination",
];

const returnFeeChargesDefination = [
  "aftermarket-parts-fee-charges-policy-defination",
  "fr-aftermarket-parts-fee-charges-policy-defination",
];

const refundContentIds = [
  "aftermarket-parts-return-policy-refunds-faq",
  "fr-aftermarket-parts-return-policy-refunds-faq",
];

const cancellationsContentIds = [
  "aftermarket-parts-return-policy-cancellation-faq",
  "fr-aftermarket-parts-return-policy-cancellation-faq",
];

const coreContentIds = [
  "aftermarket-parts-return-policy-core-returns-faq",
  "fr-aftermarket-parts-return-policy-core-returns-faq",
];

const returnContentIds = [
  "aftermarket-parts-return-policy-returns-faqs",
  "fr-aftermarket-parts-return-policy-returns-faqs",
];

const returnStepsContentIds = [
  "return-automotive-parts-steps-to-return-an-order",
  "fr-return-automotive-parts-steps-to-return-an-order",
];

function Returns(props: IAftermarketReturnPolicy) {
  const { t } = useTranslation("static-pages");
  const response = props.actualResponse.landingPage;
  const {
    headers,
    metaTitle, metaDescription, metaKeywords, responsiveBannerMedia,
  } = response;
  const { contentIds } = response.links;
  const [showYoutubeThumbnail, setShowYoutubeThumbnail] = useState(true);

  const staticContent = [
    { heading: t("Returns Process"), data: returnPolicyDefination },
    { heading: t("Fees and Charges"), data: returnFeeChargesDefination },
  ];

  const faqs = [
    { heading: t("Refunds"), data: refundContentIds },
    { heading: t("Cancellations"), data: cancellationsContentIds },
    { heading: t("Returns"), data: returnContentIds },
    { heading: t("Core"), data: coreContentIds },
  ];

  function scrollToReturnProcess() {
    scrollTo("anchor_return_process", {
      duration: 500,
      delay: 100,
      smooth: true,
      offset: -70,
    });
  }

  return (
    <PageContainer>
      <Head>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="all" />
        <meta name="keywords" content={metaKeywords} />
      </Head>

      <LandingPageBanner
        className="m-t-10"
        style={{ height: "auto" }}
        bannerImages={{
          mobileImage: getRelativeImagePath(`/cms/${responsiveBannerMedia?.squareImage.url}`),
          desktopImage: getRelativeImagePath(`/cms/${responsiveBannerMedia?.desktop.url}`),
        }}
        {...{
          width: 1340, height: 410, applyStyle: false,
        }}
        alt={headers.h1}
      />

      <Text size={1} bold className="text-center p-t-20">{headers.h1}</Text>
      <div />

      <div className="d-flex justify-content-between flex-wrap m-t-20 p-t-20">
        {
          returnProcedure.map((each, index) => (
            <div className={`d-flex flex-column ${styles.returnStep_each}`} key={each.step}>
              <div className={`${styles.return_steps_Image} ${styles[each.step]}`} />
              <Text bold className="text-center" style={{ fontSize: 16 }}>{index + 1}</Text>
              <Text className="text-center" style={{ fontSize: 16 }}>{t(each.step)}</Text>
            </div>
          ))
        }
      </div>

      <div className="p-t-30 d-flex align-items-center justify-content-center">
        <PAButton onClick={() => scrollToReturnProcess()} style={{ width: 200 }} size={BUTTON_SIZE.LARGE}>
          {t("Initiate a return")}
        </PAButton>
      </div>

      <div className="row d-flex justify-content-between flex-wrap m-t-20 p-t-20">
        {staticContent.map((el) => (
          <div key={el.heading} className="col-sm-6 d-flex m-b-5 col-xs-12">
            <div className={`d-flex p-20 p-lr-0-xs flex-column  ${styles.grey_background}`}>

              <Text bold className="text-center p-t-20">{el.heading}</Text>
              {
                contentIds.map((eachContent) => {
                  const { content, uiType } = eachContent?.uiDetails || {};
                  const contentId = eachContent.url;
                  if (el.data && !(el.data.includes(contentId))) {
                    return null;
                  }
                  if (uiType === "STATIC_PAGE") {
                    return (
                      <CmsContent key={contentId} className={styles.static_page} content={content?.article} />
                    );
                  }
                })
              }
            </div>
          </div>
        ))}
      </div>

      <div className="p-t-20 m-t-20">
        <ChatReturn />
      </div>

      <div className="d-flex justify-content-center flex-wrap m-t-20 p-t-20 align-items-center">
        <Text bold className="text-center">{t("Frequently Asked Questions")}</Text>
        <div className="row d-flex flex-wrap">
          {faqs.map((faq) => (
            <div key={faq.heading} className="d-flex flex-column align-items-center col-sm-6 col-xs-12">
              <Text bold className="text-center p-t-20">{faq.heading}</Text>
              {
                contentIds.map((eachContent) => {
                  const { content, uiType } = eachContent?.uiDetails || {};
                  const contentId = eachContent.url;
                  if (faq.data && !(faq.data.includes(contentId))) {
                    return null;
                  }
                  if (uiType === "FAQ") {
                    return (
                      <FaqAccordion key={contentId} faqs={content.faqs} />
                    );
                  }
                })
              }
            </div>
          ))}
        </div>
      </div>

      <Element
        name="anchor_return_process"
      />
      <div className={`text-center d-flex justify-content-center flex-wrap m-t-20 p-t-20 align-items-center flex-column ${styles.return_process_container}`}>
        <Text size={1} bold className="text-center p-b-20">{t("How do I Return an Item?")}</Text>
        <div style={{ width: "100%" }}>
          {showYoutubeThumbnail && (
            <div className={styles.youtube_thumbnail_container}>
              <div className={styles.youtube_thumbnail_mask}>
                <img
                  alt="Return a order"
                  src={`${process.env.NEXT_PUBLIC_ROOT_URL}/images/common/videoplay.png`}
                  onClick={() => setShowYoutubeThumbnail(false)}
                />
              </div>
            </div>
          )}
          {!showYoutubeThumbnail && (
            <iframe
              title="Return a order"
              src="https://www.youtube.com/embed/j24oByxIiaE?rel=0&showinfo=0&fs=1&autoplay=1"
              allowFullScreen
              data-clarity-mask="True"
            />
          )}
        </div>
      </div>

      <div className="d-flex justify-content-between flex-wrap m-t-20 p-t-20">
        <div className="col-sm-12  d-flex m-b-5  p-lr-0-xs  col-xs-12">
          <div className={`d-flex p-20 flex-column  ${styles.grey_background}`}>
            <Text bold className="p-t-20 p-b-20">{t("Steps to Return an Order")}</Text>
            {
              contentIds.map((eachContent) => {
                const { content, uiType } = eachContent?.uiDetails || {};
                const contentId = eachContent.url;
                if (returnStepsContentIds && !(returnStepsContentIds.includes(contentId))) {
                  return null;
                }
                if (uiType === "STATIC_PAGE") {
                  return (
                    <CmsContent key={contentId} className={styles.static_page} content={content?.article} />
                  );
                }
              })
            }
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

export default memo(Returns);
